import {
  DAY_ZERO_TOUR_ID,
  dayZeroTourConfig
} from '../components/guided-tour/day-zero-tour.config';
import type {
  EngineTourConfig,
  EngineTourStep,
  TourEngineStatus
} from '../components/guided-tour/tour-engine.types';
import {
  TRIAL_TOUR_ID,
  trialTourConfig
} from '../components/guided-tour/trial-tour.config';
import { logError } from '../helpers/logger.helper';
import { createPersistedStore, type Store } from './store-creators';

/** All registered tour ids — the exhaustive set `start`/`resume` accept. */
export type TourId = typeof DAY_ZERO_TOUR_ID | typeof TRIAL_TOUR_ID;

/** All registered tours, keyed by tourId. */
export const TOURS: Record<TourId, EngineTourConfig> = {
  [DAY_ZERO_TOUR_ID]: dayZeroTourConfig,
  [TRIAL_TOUR_ID]: trialTourConfig
};

type TourEngineState = {
  /** The tour currently launched, or null when idle. */
  activeTourId: TourId | null;
  status: TourEngineStatus;
  stepIndex: number;
  /**
   * Tours already offered — marked on begin, dismiss or complete, never on
   * start, so a reload mid-welcome re-offers but a declined tour stays declined.
   * Persisted separately from the triplet, which resets to idle on reload
   * unless the tour was mid-flight.
   */
  offeredTourIds: string[];
  /**
   * Whether this browser had already completed a trial before the current
   * landing. Captured once, in `useTrialEntry`, from the `moderne_try_seen`
   * cookie's state *before* that same call writes it — reading the cookie
   * later (e.g. from the welcome modal) would always see it as already set.
   * Not persisted; `null` outside an active trial landing.
   */
  trialIsReturning: boolean | null;
  /** Launch a tour: shows its welcome modal before the first step. */
  start: (tourId: TourId) => void;
  /** Leave the welcome modal and begin the first step ("Show me how"). */
  begin: () => void;
  /** Re-enter a tour: continue an in-progress one, else relaunch from welcome. */
  resume: (tourId: TourId) => void;
  /** Advance to the next step; completes the tour past the last one. */
  advance: () => void;
  /** Step back to the current step's declared safe re-entry point (`backTo`). */
  goBack: () => void;
  /** Jump to a specific step by id (e.g. straight to the recipe page on select). */
  goToStep: (stepId: string) => void;
  /** Rewind to the current step's `resumeAnchor` when its target vanished. */
  recoverToAnchor: () => void;
  /** Pause when a step's target never appeared; resumable from the Help menu. */
  pause: () => void;
  /** End the tour as skipped / closed. */
  dismiss: () => void;
  /** End the tour as successfully completed (shows the completion modal). */
  complete: () => void;
  /** Record whether the current trial landing is a repeat visit (see field doc). */
  setTrialIsReturning: (value: boolean) => void;
};

/**
 * Looks up a tour config by a possibly-unrecognized id — persisted state can
 * name a tour that's since been removed from `TOURS`, so this stays permissive
 * (`string`, not `TourId`) at this one boundary rather than trusting the cast
 * `mergePersisted` applies to untrusted deserialized data.
 */
const tourConfigFor = (tourId: string | null): EngineTourConfig | undefined =>
  tourId ? TOURS[tourId as TourId] : undefined;

const stepCountFor = (tourId: string | null): number =>
  tourConfigFor(tourId)?.steps.length ?? 0;

/** Index of the step with the given id, or -1 (also -1 when steps is absent). */
export const findStepIndexById = (
  steps: EngineTourStep[] | undefined,
  id: string
): number => steps?.findIndex((step) => step.id === id) ?? -1;

/** Step-id reference fields on a step's `data`, resolved to an earlier index. */
export type StepRefField = 'backTo' | 'resumeAnchor';

/**
 * Resolve a step's `backTo` / `resumeAnchor` reference to the index of the
 * strictly-earlier step it names. Returns -1 when the field is unset; also
 * returns -1 (and logs) when it's set but doesn't resolve backward — a config
 * mistake the invariant test guards, surfaced at runtime as defense in depth.
 */
export const resolveEarlierStep = (
  steps: EngineTourStep[] | undefined,
  index: number,
  field: StepRefField
): number => {
  const refId = steps?.at(index)?.data[field];
  if (!refId) {
    return -1;
  }
  const target = findStepIndexById(steps, refId);
  if (target < 0 || target >= index) {
    logError({
      name: 'TourStepRefUnresolved',
      message: `Step ${index} ${field} "${refId}" does not resolve to an earlier step.`
    });
    return -1;
  }
  return target;
};

/**
 * The step to actually resume on. If the persisted step's target lives inside a
 * dialog/drawer (closed after a reload), rewind to its always-present
 * `resumeAnchor` so the tour doesn't stall on a target that can't reappear.
 */
export const resumeIndexFor = (
  activeTourId: string | null,
  stepIndex: number
): number => {
  const steps = tourConfigFor(activeTourId)?.steps;
  const anchorIndex = resolveEarlierStep(steps, stepIndex, 'resumeAnchor');
  return anchorIndex >= 0 ? anchorIndex : stepIndex;
};

/**
 * True when a (persisted) triplet represents an in-progress, in-range tour.
 * Doubles as the rehydration validator for the triplet: a corrupt activeTourId,
 * status or stepIndex cannot reach state. The offer log is checked separately.
 */
const isResumable = (
  s: Pick<TourEngineState, 'activeTourId' | 'status' | 'stepIndex'>
): boolean =>
  typeof s.activeTourId === 'string' &&
  (s.status === 'running' || s.status === 'paused') &&
  Number.isInteger(s.stepIndex) &&
  s.stepIndex >= 0 &&
  s.stepIndex < stepCountFor(s.activeTourId);

const markOffered = (
  state: Pick<TourEngineState, 'activeTourId' | 'offeredTourIds'>
): Pick<TourEngineState, 'offeredTourIds'> => {
  const { activeTourId, offeredTourIds } = state;
  if (!activeTourId || offeredTourIds.includes(activeTourId)) {
    return { offeredTourIds };
  }
  return { offeredTourIds: [...offeredTourIds, activeTourId] };
};

/** A tour the user acted on, so its welcome modal should not be re-offered. */
const RESOLVED_STATUSES: readonly TourEngineStatus[] = [
  'running',
  'paused',
  'dismissed',
  'completed'
];

/**
 * The offer log to rehydrate. Payloads written before the log existed carry no
 * `offeredTourIds`, so seed it from a tour that already reached a resolved
 * status — otherwise everyone who took Day Zero before this shipped gets
 * re-offered it once. `welcome` is excluded: that user never acted (#8715).
 */
const offerLogFrom = (
  p: Partial<TourEngineState>,
  current: Store<TourEngineState>
): string[] => {
  if (Array.isArray(p.offeredTourIds)) {
    return p.offeredTourIds.filter((id) => typeof id === 'string');
  }
  if (
    typeof p.activeTourId === 'string' &&
    p.status &&
    RESOLVED_STATUSES.includes(p.status)
  ) {
    return [p.activeTourId];
  }
  return current.offeredTourIds;
};

/**
 * Rehydrate only a genuinely in-progress, in-range tour across reloads. Anything
 * else — completed/dismissed/welcome/idle, a corrupt shape, or a stale
 * out-of-range index after the config shrank — starts fresh at idle.
 */
export const mergePersisted = (
  persisted: unknown,
  current: Store<TourEngineState>
): Store<TourEngineState> => {
  const p = (persisted ?? {}) as Partial<TourEngineState>;
  const triplet = {
    activeTourId: p.activeTourId ?? null,
    status: p.status ?? 'idle',
    stepIndex: p.stepIndex ?? 0
  };
  // The offer log survives regardless of the triplet: a tour the user already
  // declined must not be auto-offered again just because its status rehydrates
  // to idle (#8715).
  const withOffers = { ...current, offeredTourIds: offerLogFrom(p, current) };
  if (!isResumable(triplet)) {
    return withOffers;
  }
  // Reload lands with every dialog/drawer closed, so rewind a dialog-internal
  // step to its always-present anchor instead of stalling on a lost target.
  return {
    ...withOffers,
    ...triplet,
    stepIndex: resumeIndexFor(triplet.activeTourId, triplet.stepIndex)
  };
};

export const useTourEngineStore = createPersistedStore<TourEngineState>(
  (set, get) => ({
    activeTourId: null,
    status: 'idle',
    stepIndex: 0,
    offeredTourIds: [],
    trialIsReturning: null,
    start: (tourId) =>
      set({ activeTourId: tourId, status: 'welcome', stepIndex: 0 }),
    begin: () => set((state) => ({ status: 'running', ...markOffered(state) })),
    resume: (tourId) => {
      const state = get();
      // Continue an in-progress tour; otherwise (re)launch from the welcome modal.
      if (state.activeTourId === tourId && isResumable(state)) {
        set({
          status: 'running',
          stepIndex: resumeIndexFor(state.activeTourId, state.stepIndex)
        });
      } else {
        set({ activeTourId: tourId, status: 'welcome', stepIndex: 0 });
      }
    },
    advance: () => {
      const { activeTourId, stepIndex } = get();
      const next = stepIndex + 1;
      if (next >= stepCountFor(activeTourId)) {
        set({ status: 'completed' });
      } else {
        set({ stepIndex: next });
      }
    },
    goBack: () => {
      const { activeTourId, status, stepIndex } = get();
      if (status !== 'running' || !activeTourId || stepIndex <= 0) {
        return;
      }
      const resolved = resolveEarlierStep(
        TOURS[activeTourId]?.steps,
        stepIndex,
        'backTo'
      );
      // Prefer the declared safe re-entry point, but always move backward: a
      // goBack that changed nothing would strand react-joyride at a COMPLETE
      // lifecycle (in controlled mode the COMPLETE→INIT self-heal is skipped),
      // leaving an invisible, full-page click shield only a reload can clear.
      set({ stepIndex: resolved >= 0 ? resolved : stepIndex - 1 });
    },
    goToStep: (stepId) => {
      const { activeTourId, status } = get();
      if (status !== 'running' || !activeTourId) {
        return;
      }
      const index = findStepIndexById(TOURS[activeTourId]?.steps, stepId);
      if (index >= 0) {
        set({ stepIndex: index });
      }
    },
    recoverToAnchor: () => {
      const { activeTourId, status, stepIndex } = get();
      if (status !== 'running' || !activeTourId) {
        return;
      }
      // If the current step's target vanished (e.g. the user dismissed the
      // search dialog without picking the recipe), rewind to its always-present
      // anchor instead of leaving the tooltip stranded at the top-left.
      const target = resumeIndexFor(activeTourId, stepIndex);
      if (target !== stepIndex) {
        set({ stepIndex: target });
      }
    },
    pause: () => set({ status: 'paused' }),
    dismiss: () =>
      set((state) => ({ status: 'dismissed', ...markOffered(state) })),
    // Defence in depth: begin()/resume()/rehydration all mark it first, but
    // advance() completes without marking, so do it here too.
    complete: () =>
      set((state) => ({ status: 'completed', ...markOffered(state) })),
    setTrialIsReturning: (value) => set({ trialIsReturning: value })
  }),
  {
    name: 'tour-engine',
    // Persist the resumable triplet plus the offer log; action functions are
    // recreated on load.
    partialize: (state) =>
      ({
        activeTourId: state.activeTourId,
        status: state.status,
        stepIndex: state.stepIndex,
        offeredTourIds: state.offeredTourIds
      }) as unknown as Store<TourEngineState>,
    merge: (persisted, current) => mergePersisted(persisted, current)
  }
);

/** Derived: the active step's id, or null when the tour is not running. */
export const selectActiveStepId = (state: TourEngineState): string | null => {
  if (state.status !== 'running' || !state.activeTourId) {
    return null;
  }
  return TOURS[state.activeTourId]?.steps.at(state.stepIndex)?.id ?? null;
};
